import type { Table } from '@w/db-replication'
import { createDb, customIndex, generateDDL, index, sql, type DatabaseDriver, type ColumnMetadata } from '@w/db'
import { sqliteTable } from '@w/db/sqlite'
import { s } from '@w/schema'
import {
  cardKindCodes, flashcardDifficultyCodes,
  flashcardEventDataSchema, flashcardImportanceCodes,
} from '../server/db/flashcards.schema'
import { learnerPromptTypeCodes } from './card-history'
import { FLASHCARDS_EMBEDDING_INDEX, pageEmbeddingIndexOptions } from './embedding-schema'
import { NEMOTRON_EMBEDDING_INDEX } from '../search/embedding-protocol'

// INV-1: structure lives in the CRDT; product scalars have one plain replicated column.
export const pages = sqliteTable({
  columns: (c) => ({
    id: c.text().notNull().primaryKey(),
    data: c.crdt().notNull(),
    updatedAt: c.datetime().notNull(),
    title: c.text().notNull().default('Untitled page'),
    titleKey: c.text().notNull().default('untitled page'),
    kind: c.text().notNull(),
    deletedAt: c.integer(),
    deletedBy: c.text(),
    archived: c.boolean().notNull().default(false),
    translatable: c.boolean().notNull().default(false),
    language: c.text(),
    inheritance: c.text().notNull().default('inherit').$type<'inherit' | 'restricted'>(),
    editedBy: c.text().notNull().default(''),
    entryId: c.text(),
    pos: c.text(),
    inflectionClass: c.text(),
    headword: c.text(),
    headwordKey: c.text(),
    transcription: c.text(),
    audioSrc: c.text(),
    readAloudQuestionId: c.text(),
    dictationQuestionId: c.text(),
    entryKind: c.text(),
    translations: c.json(s.record(s.string(), s.string())),
    // Last: an appended ALTER TABLE ADD COLUMN puts it after `translations` on migrated replicas (202611010001_page_readonly.sql).
    /** Opens the page in read mode; a rendering hint, never checked on writes (flashcards-editor-home.spec.md "Read and edit modes"). */
    readonly: c.boolean().notNull().default(false),
  }),
  indexes: (t) => [
    index(t.kind), index(t.deletedAt), index(t.updatedAt),
    index(t.entryId), index(t.language, t.headword),
    customIndex({ name: 'idx_pages_crdt_attr', on: [t.data], using: 'crdt_attr', with: sql.raw`keys = 'qid,pageId,src,cefr,senseRecallQuestionId,formRecallQuestionId'` }),
    customIndex({ name: 'lex_trgm', on: [t.headwordKey], using: 'trigram', with: sql.raw`threshold = 0.3` }),
  ],
})

/** Device-local search projection of pages (flashcards-search.decisions.md "Batched search projection"): FTS and
 * embeddings index this copy, refreshed once per changed page by refreshPageSearch, never per replayed edit. */
export const pageSearch = sqliteTable({
  columns: c => ({
    id: c.text().notNull().primaryKey(),
    title: c.text().notNull(),
    text: c.text().notNull(),
    searchVersion: c.integer().notNull(),
    searchUpdatedAt: c.integer().notNull(),
    deletedAt: c.integer(),
  }),
  indexes: t => [
    customIndex({ name: 'idx_pagesearch_fts', on: [t.title, t.text], using: 'fts', with: sql.raw`tokenizer = 'simple'` }),
    customIndex({ name: FLASHCARDS_EMBEDDING_INDEX.name, on: [t.title, t.text], using: 'embedding', with: sql.raw(pageEmbeddingIndexOptions(FLASHCARDS_EMBEDDING_INDEX)), where: sql`${t.deletedAt} IS NULL` }),
    customIndex({ name: NEMOTRON_EMBEDDING_INDEX.name, on: [t.title, t.text], using: 'embedding', with: sql.raw(pageEmbeddingIndexOptions(NEMOTRON_EMBEDDING_INDEX)), where: sql`${t.deletedAt} IS NULL` }),
  ],
})

export const genericCards = sqliteTable({
  columns: (c) => ({
    qid: c
      .text()
      .notNull()
      .comment('Question block reference (content)'),
    fractionalIndex: c
      .text()
      .notNull()
      .comment('Source-order sort key from question block'),

    pageId: c.text().notNull(),

    importance: c.enum(flashcardImportanceCodes).notNull().default('medium'),
    difficulty: c.enum(flashcardDifficultyCodes).notNull().default('medium'),
    promptType: c
      .enum(learnerPromptTypeCodes)
      .notNull()
      .default('short-answer')
      .comment('Prompt strategy name from block metadata'),
    stage: c
      .integer()
      .notNull()
      .default(0)
      .comment('Current stage in SRS algorithm'),
    repeatAt: c.custom('INTEGER', s.union([s.date(), s.literal('now')]), {
      encode: value => value === 'now' ? 'now' : value.getTime(),
      decode: value => value === 'now' ? 'now' as const : new Date(Number(value)),
    }).notNull().default('now').comment('Logical now until first learner action'),
    ttaSeconds: c.integer().notNull().default(30).comment('Time taken for user to answer'),

    frozenAt: c
      .datetime()
      .comment('Frozen cards are never shown during training'),
    deletedAt: c
      .datetime()
      .comment(
        'To delete a card you need to delete its question block. Soft delete to support cut&paste or undo',
      ),
    deletedBy: c
      .text()
      .comment(
        'When a user deletes a card in workspace other users need to see it during training',
      ),
    updatedAt: c.datetime().notNull(),
    touchedAt: c.datetime(),
    graduated: c.boolean().notNull().default(false),

    cardKind: c
      .enum(cardKindCodes)
      .comment(
        'Sitting order for lexicon cards; NULL means note card. Written by the lexicon page commit, never derived from page values.',
      ),
    node: c.text(),
    acceptedRemoval: c.text().comment('The removal Accept accepted: page@deletedAt or node@removedAt (learnerCards)'),
  }),
  primaryKey: t => [t.qid],
  indexes: t => [index(t.pageId)],
})

export const flashcards = sqliteTable({
  comment: 'Owner-written replicated learner cards',
  columns: (c) => ({
    qid: c
      .text()
      .notNull()
      .primaryKey()
      .comment('Question block reference (content)'),
    fractionalIndex: c
      .text()
      .notNull()
      .comment('Source-order sort key from question block'),

    pageId: c.text().notNull(),

    importance: c.enum(flashcardImportanceCodes).notNull().default('medium'),
    difficulty: c.enum(flashcardDifficultyCodes).notNull().default('medium'),
    promptType: c
      .enum(learnerPromptTypeCodes)
      .notNull()
      .default('short-answer')
      .comment('Prompt strategy name from block metadata'),
    stage: c
      .integer()
      .notNull()
      .default(0)
      .comment('Current stage in SRS algorithm'),
    repeatAt: c.custom('INTEGER', s.union([s.date(), s.literal('now')]), {
      encode: value => value === 'now' ? 'now' : value.getTime(),
      decode: value => value === 'now' ? 'now' as const : new Date(Number(value)),
    }).notNull().default('now').comment('Logical now until first learner action'),
    ttaSeconds: c.integer().notNull().default(30).comment('Time taken for user to answer'),

    userId: c.text().notNull().comment('User who trains the card'),
    frozenAt: c
      .datetime()
      .comment('Frozen cards are never shown during training'),
    deletedAt: c
      .datetime()
      .comment(
        'To delete a card you need to delete its question block. Soft delete to support cut&paste or undo',
      ),
    deletedBy: c
      .text()
      .comment(
        'When a user deletes a card in workspace other users need to see it during training',
      ),
    updatedAt: c.datetime().notNull(),
    touchedAt: c.datetime(),
    graduated: c.boolean().notNull().default(false),

    cardKind: c
      .enum(cardKindCodes)
      .comment(
        'Sitting order for lexicon cards; NULL means note card. Written by the lexicon page commit, never derived from page values.',
      ),
    node: c.text(),
    acceptedRemoval: c.text().comment('The removal Accept accepted: page@deletedAt or node@removedAt (learnerCards)'),
    // Last: an appended ALTER TABLE ADD COLUMN puts it after `acceptedRemoval` on migrated replicas (202611030001_flashcard_review_count.sql).
    /** The learner's reviews of this card; the practice question of a rule or question group shown next is reviewCount mod their count. */
    reviewCount: c.integer().notNull().default(0),
  }),
  indexes: (t) => [
    index(t.pageId, t.node),
    index(t.userId, t.pageId, t.node, t.qid),
    index(t.deletedAt),
    index(t.updatedAt),
    customIndex({
      on: [t.userId, t.repeatAt, t.fractionalIndex, t.qid],
    }),
    customIndex({
      on: [
        t.userId,
        t.cardKind,
        t.repeatAt,
        t.fractionalIndex,
        t.qid,
      ],
    }),
    customIndex({
      on: [t.pageId, t.fractionalIndex, t.qid],
    }),
  ],
  fn: () => ({
    secure: () => true,
  }),
})

export const flashcardEvents = sqliteTable({
  comment: 'Append-only review history; never a source of card state',
  columns: c => ({
    id: c.text().notNull().primaryKey(),
    occurredAt: c.datetime().notNull(),
    qid: c.text().notNull(),
    deviceId: c.text().notNull(),
    stageAt: c.integer().notNull(),
    userId: c.text().notNull(),
    data: c.json(flashcardEventDataSchema).notNull(),
  }),
  indexes: t => [index(t.userId, t.qid, t.stageAt, t.occurredAt, t.id)],
  fn: () => ({ secure: () => true }),
})

/** Replicated placement and content-derived links. */
export const pageRelations = sqliteTable({
  columns: c => ({
    fromPageId: c.text().notNull(),
    toPageId: c.text().notNull(),
    targetSpaces: c.text().notNull().default('::').comment('Colon-delimited hex target spaces; empty qualifier means relative link'),
    type: c.custom("TEXT CHECK(type IN ('child','link'))", s.enum(['child', 'link'])).notNull(),
    node: c.text().notNull().default(''),
  }),
  primaryKey: t => [t.fromPageId, t.toPageId, t.type, t.node],
  indexes: t => [index(t.toPageId, t.type, t.fromPageId), customIndex({ name: 'idx_pageRelations_single_parent', on: [t.toPageId], unique: true, where: sql.raw`type='child'` })],
})

export const pagePermissions = sqliteTable({
  columns: c => ({ pageId: c.text().notNull(), principalKind: c.text().notNull().$type<'user'>(), principalId: c.text().notNull(), level: c.integer().notNull() }),
  primaryKey: t => [t.pageId,t.principalKind,t.principalId],
  indexes: t => [index(t.principalKind,t.principalId,t.pageId,t.level)],
})

/** Read-only derived translations; source content remains in pages. */
export const pageTranslations = sqliteTable({
  columns: c => ({ pageId: c.text().notNull(), blockId: c.text().notNull(), language: c.text().notNull(),
    sourceHash: c.text().notNull(), text: c.text(), detectedSourceLanguage: c.text() }),
  primaryKey: t => [t.pageId, t.blockId, t.language],
  indexes: t => [index(t.pageId, t.language)],
})

/**
 * Content of a removed question part, written by the remover's commit (db-crdt-sync requirements :49): a page freeze
 * folds tombstone content away (requirements :26), and deleted review, Clone and Restore still read it (spaces spec
 * INV-L7, :145). Page-scoped like pageTranslations; a live re-insert of the qid on the page deletes its rows.
 */
export const retainedParts = sqliteTable({
  columns: c => ({ pageId: c.text().notNull(), qid: c.text().notNull(), node: c.text().notNull(), content: c.text().notNull(),
    removedBy: c.text().notNull(), removedAt: c.integer().notNull() }),
  primaryKey: t => [t.pageId, t.qid, t.node],
})

/** Space-owned custom database DDL history, replicated with the workspace. */
export const cdbDdlLog = sqliteTable({
  columns: c => ({
    id: c.text().primaryKey(),
    pageId: c.text().notNull(),
    tableName: c.text().notNull(),
    original: c.text().notNull(),
    expanded: c.text().notNull(),
    createdAt: c.integer().notNull(),
  }),
})

/** Derived search state is device-local; every product table is replicated. */
export const flashcardsLocalTables: string[] = ['pageSearch', 'genericCards', 'pageLiveEdits']
export const workspaceTables = { pages, genericCards, flashcards, flashcardEvents, pageRelations, pagePermissions, pageTranslations, retainedParts, __cdb_ddl_log: cdbDdlLog, pageSearch }

/**
 * How each replicated row is reached (db-crdt-sync.spec.md "Resource kinds and keys", requirements 2026-11-02 :71-:72):
 * a shareable resource is reached through page permissions, `page` naming its page id column; a private resource
 * belongs to the user in `owner`; an attached row is no resource and hangs off the page in `page`, or off one page of
 * the edge in `edge` (a child edge off its child, a link off its source). `key` is the device primary key; a private
 * row's server key is `[owner, ...key]`; `users` are key columns that hold a user id; an `appendOnly` table's rows are
 * inserted, never updated or deleted. Server layouts, admission, the
 * row index and the read rule read this one map (prior art: PowerSync sync rules, Zero permissions: one table → rule
 * map); custom database rows are shareable rows of tables the map cannot name (custom-databases domain K8).
 */
export type ReplicatedRow =
  | { kind: 'shareable' | 'attached'; key: readonly string[]; page: string; users?: readonly string[] }
  | { kind: 'attached'; key: readonly string[]; edge: readonly [from: string, to: string, type: string] }
  | { kind: 'private'; key: readonly string[]; owner: 'userId'; appendOnly?: true }
export const replicatedRows = {
  pages: { kind: 'shareable', key: ['id'], page: 'id' },
  flashcards: { kind: 'private', key: ['qid'], owner: 'userId' },
  flashcardEvents: { kind: 'private', key: ['id'], owner: 'userId', appendOnly: true },
  pageRelations: { kind: 'attached', key: ['fromPageId', 'toPageId', 'type', 'node'], edge: ['fromPageId', 'toPageId', 'type'] },
  pagePermissions: { kind: 'attached', key: ['pageId', 'principalKind', 'principalId'], page: 'pageId', users: ['principalId'] },
  pageTranslations: { kind: 'attached', key: ['pageId', 'blockId', 'language'], page: 'pageId' },
  retainedParts: { kind: 'attached', key: ['pageId', 'qid', 'node'], page: 'pageId' },
  __cdb_ddl_log: { kind: 'attached', key: ['id'], page: 'pageId' },
} as const satisfies Record<Exclude<keyof typeof workspaceTables, 'genericCards' | 'pageSearch'>, ReplicatedRow>
export type ReplicatedTableName = keyof typeof replicatedRows

// Physical replicated columns; virtual structure indexes remain local.
export const replicatedTables: (Omit<Table,'primaryKey'> & {primaryKey:string[]; row: ReplicatedRow})[] = (Object.keys(replicatedRows) as ReplicatedTableName[])
  .map(name => ({
    name,
    primaryKey: [...replicatedRows[name].key],
    row: replicatedRows[name],
    columns: Object.entries<{ metadata: ColumnMetadata }>(workspaceTables[name].__columns).filter(([, column]) => !column.metadata.generated)
      .map(([name, column]) => {
        const type = column.metadata.dataType.toLowerCase()
        return { name, type: (type.startsWith('vector') ? 'blob' : type) as Table['columns'][number]['type'] }
      }),
  }))


export function createWorkspaceDb(driver: DatabaseDriver) {
  return createDb(workspaceTables, { driver })
}

export type WorkspaceDb = ReturnType<typeof createWorkspaceDb>
export type WorkspacePage = typeof pages.__selectType

/** Includes the FTS index, declared through the ORM's customIndex builder. */
export function workspaceDdl(db: WorkspaceDb): string {
  return generateDDL(db)
}

export function clearWorkspaceDb(db: WorkspaceDb) {
  return db.transaction(async (tx) => {
    await tx.query`DELETE FROM ${tx.flashcardEvents}`.run()
    await tx.query`DELETE FROM ${tx.flashcards}`.run()
    await tx.query`DELETE FROM ${tx.genericCards}`.run()
    await tx.query`DELETE FROM ${tx.pages}`.run()
    await tx.query`DELETE FROM ${tx.pageRelations}`.run()
  })
}

/** One fragment per table, as Drizzle/Kysely compile a select once: the column list is static, and folding it
 * per read rebuilt a nested fragment per column (27 columns: 11.5 us a call). */
const pageSourceFragments = new WeakMap<WorkspaceDb['pages'], ReturnType<typeof sql>>()

/** Page source and its snapshot-header version; search text lives only in pageSearch. */
export function pageSourceColumns(table: WorkspaceDb['pages']) {
  const cached = pageSourceFragments.get(table)
  if (cached) return cached
  const physical = Object.values(table.__columns).filter(column => column !== table.id)
    .reduce((columns, column) => sql`${columns}, ${column === table.title ? sql`${column} AS title` : sql`${column}`}`, sql`${table.id}`)
  const fragment = sql`${physical}`
  pageSourceFragments.set(table, fragment)
  return fragment
}
